import { Button, cn } from '@parrot-software/design-system';
import { ArrowLeft } from '@parrot-software/design-system/icons';
import { useTranslation } from 'react-i18next';

type WizardNavigationProps = {
  disabled?: boolean;
  isFirstStep?: boolean;
  isLast: boolean;
  isLoading: boolean;
  onBack?: () => void;
  onNext: () => void;
};

/**
 * Navigation row for the wizard — single primary action aligned to the right on lg+,
 * full-width on mobile/tablet. On desktop an inline back button is rendered to the left;
 * mobile/tablet keep the back action inside WizardHeader.
 */
export const WizardNavigation = ({
  disabled = false,
  isFirstStep = false,
  isLast,
  isLoading,
  onBack,
  onNext,
}: WizardNavigationProps) => {
  const { t } = useTranslation('onboarding');
  const isDisabled = disabled || isLoading;

  const buttonLabel = isLoading ? t('wizard.saving') : isLast ? t('wizard.finish') : t('wizard.continue');

  return (
    <div className="flex flex-col-reverse items-stretch gap-2 pt-4 lg:flex-row lg:items-center">
      {onBack && (
        <Button
          aria-label={t('wizard.back')}
          className={cn('hidden size-9 p-2 lg:mr-auto lg:inline-flex', { 'lg:invisible': isFirstStep })}
          disabled={isLoading || isFirstStep}
          onClick={onBack}
          variant="outline"
        >
          <ArrowLeft size={16} />
        </Button>
      )}
      <Button
        className="w-full gap-2 bg-primary pl-4 pr-3 hover:bg-primary/90 disabled:opacity-50 lg:w-auto"
        disabled={isDisabled}
        onClick={onNext}
      >
        {buttonLabel}
        {!isLoading && (
          <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        )}
      </Button>
    </div>
  );
};
